import type { DealsEnum } from '@b-stock/search-api-client'

import { getLowBidderDealEligibility } from '@/helpers/deals/getLowBidderDealEligibility'
import type { LowBidderDealEligibility } from '@/helpers/deals/getLowBidderDealEligibility'
import { isTopTierDeal } from '@/helpers/deals/isTopTierDeal'
import type {
  FmvDealBadgeConfig,
  LowBidderDealBadgeConfig,
} from '@/helpers/featureFlags'
import { ScreenName } from '@/helpers/telemetry/ScreenName'

export interface DealBadgeData {
  shouldShowDealBadge: boolean
  dealLevel: DealsEnum | null | undefined
  deal_badge_value: string
  is_deal_badge_shown: boolean
}

/** Which badge rendered, in analytics' terms (GLOB-5164). */
export type DealBadgeType = 'low_bidder' | 'fmv_hot_deal' | 'none'

/**
 * `DealBadgeData` plus the fields only analytics reads. A separate type so the
 * LDP's `DealBadgeData` does not gain them.
 */
export type ResolvedDealBadge = DealBadgeData & {
  dealBadgeType: DealBadgeType
  /**
   * Computed on the search-results screens whether or not a badge renders: in
   * the A/B control arm it is the only thing identifying the eligible cohort.
   * `null` otherwise.
   */
  lowBidder: LowBidderDealEligibility | null
}

export type ListingWithDeal = Parameters<
  typeof getLowBidderDealEligibility
>[0] & {
  storefrontSlug?: string
}

/** The flag values `resolveDealBadge` reads; see `useDealBadgeFlags`. */
export type DealBadgeFlags = {
  /** ENABLE_FMV_DEAL_BADGE_ALP */
  alp: FmvDealBadgeConfig
  /** ENABLE_SELLER_STOREFRONT_DEALS_BADGE */
  storefront: FmvDealBadgeConfig
  /** ENABLE_LOW_BIDDER_DEAL_BADGE_ALP */
  lowBidder: LowBidderDealBadgeConfig
}

/**
 * `deal_badge_value` is the deal tier when the badge shows, otherwise the
 * literal 'No Badge' that analytics expects.
 */
export const toBadgeData = (
  shouldShow: boolean,
  deal: string | null | undefined
): DealBadgeData => ({
  shouldShowDealBadge: shouldShow,
  dealLevel: deal as DealsEnum | null | undefined,
  deal_badge_value: shouldShow && deal ? deal : 'No Badge',
  is_deal_badge_shown: shouldShow,
})

export const NO_BADGE: DealBadgeData = toBadgeData(false, undefined)

/**
 * Determines deal badge visibility and tracking properties — the single source
 * of truth for both display and analytics. Pure, so code that resolves many
 * listings at once (impression loops) gets the same answer as a card; React
 * callers use `useDealBadgeData`.
 *
 * Flag precedence by screen: seller storefront cards use
 * ENABLE_SELLER_STOREFRONT_DEALS_BADGE; ALP grid cards use
 * ENABLE_LOW_BIDDER_DEAL_BADGE_ALP while it is enabled (GLOB-5164); every
 * other surface — and a disabled low-bidder flag — falls back to
 * ENABLE_FMV_DEAL_BADGE_ALP.
 */
export const resolveDealBadge = (
  listing: ListingWithDeal | undefined,
  screenName: ScreenName | undefined,
  flags: DealBadgeFlags
): ResolvedDealBadge => {
  if (!listing) {
    return { ...NO_BADGE, dealBadgeType: 'none', lowBidder: null }
  }

  // Ignores `flags.lowBidder.enabled` and the render decision; see
  // `ResolvedDealBadge.lowBidder`.
  const lowBidder =
    screenName === ScreenName.AllAuction ||
    screenName === ScreenName.SellerStorefront
      ? getLowBidderDealEligibility(listing, {
          bidderThresholdsByCategory:
            flags.lowBidder.bidderThresholdsByCategory,
          excludedStorefrontIds: flags.lowBidder.excludedStorefrontIds,
        })
      : null

  const resolved = (
    shouldShow: boolean,
    badgeType: Exclude<DealBadgeType, 'none'>
  ): ResolvedDealBadge => ({
    ...toBadgeData(shouldShow, listing.deal),
    dealBadgeType: shouldShow ? badgeType : 'none',
    lowBidder,
  })

  if (screenName === ScreenName.SellerStorefront) {
    const isExcludedById = Boolean(
      listing.storefrontId &&
      flags.storefront.excludedStorefrontIds?.includes(listing.storefrontId)
    )
    const isExcludedBySlug = Boolean(
      listing.storefrontSlug &&
      flags.storefront.excludedStorefrontSlugs?.includes(listing.storefrontSlug)
    )
    const isExcluded = isExcludedById || isExcludedBySlug
    const shouldShow =
      flags.storefront.enabled && isTopTierDeal(listing.deal) && !isExcluded
    return resolved(shouldShow, 'fmv_hot_deal')
  }

  // Low-bidder deal badge (GLOB-5164), ALP grid cards only. A disabled flag is
  // the A/B control arm: fall through so control matches pre-flag behavior.
  if (screenName === ScreenName.AllAuction && flags.lowBidder.enabled) {
    const shouldShow = lowBidder?.eligible === true
    return resolved(shouldShow, 'low_bidder')
  }

  const isStorefrontExcludedById = Boolean(
    listing.storefrontId &&
    flags.alp.excludedStorefrontIds?.includes(listing.storefrontId)
  )

  const isStorefrontExcludedBySlug = Boolean(
    listing.storefrontSlug &&
    flags.alp.excludedStorefrontSlugs?.includes(listing.storefrontSlug)
  )

  const isExcluded = isStorefrontExcludedById || isStorefrontExcludedBySlug
  const shouldShow =
    flags.alp.enabled && isTopTierDeal(listing.deal) && !isExcluded

  return resolved(shouldShow, 'fmv_hot_deal')
}
